// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CloseOutlined } from '@/constants/hardfin-standard-icons';
import { ProjectLineItem } from '@/project/api/types';
import { TransactionTypeChip } from '@/project/components/transaction-type-chip';
import { formatNumber } from '@/utilities/number';
import { Box, IconButton, Typography } from '@mui/material';
import { Interval } from 'luxon';

interface Props {
  lineItem: ProjectLineItem | null;
  onClose: () => void;
}

export function BillingPlanDrawerHeader(props: Props) {
  const start = props.lineItem?.startOn ?? null;
  const end = props.lineItem?.endOn ?? null;
  const dateRange =
    start && end
      ? Interval.fromDateTimes(start, end).toLocaleString({
          dateStyle: 'medium',
        })
      : start
        ? start.toLocaleString({ dateStyle: 'medium' })
        : end
          ? end.toLocaleString({ dateStyle: 'medium' })
          : '';

  return (
    <>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ flex: 1, minWidth: 0 }}
        >
          Billing plan
        </Typography>
        <IconButton onClick={props.onClose} size="small">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body2">
                {props.lineItem?.item.sku}
              </Typography>
              {props.lineItem && (
                <TransactionTypeChip
                  transactionType={props.lineItem.transactionType}
                />
              )}
            </Box>
            <Typography
              sx={{ mt: 0.25, overflowWrap: 'anywhere' }}
              variant="body2"
              color="text.secondary"
            >
              {props.lineItem?.item.name}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            {props.lineItem?.quantity != null && (
              <Typography variant="body2" sx={{ mb: dateRange ? 0.25 : 0 }}>
                {formatNumber(props.lineItem.quantity)} units
              </Typography>
            )}
            {dateRange && (
              <Typography
                variant="body2"
                color="text.secondary"
                display="block"
              >
                {dateRange}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
